import { useCallback, useContext, useEffect, useState } from "react";
import { AppLayout } from "../../../containers/layout/app";
import { VaultWrapper } from "./styled";
import { AppContext } from "../../../context/appContext";
import {
	Box,
	Card,
	CardContent,
	CircularProgress,
	Grid2,
	SelectChangeEvent,
	Stack,
	Typography,
} from "@mui/material";
import { BaseButton } from "../../../components/button/styled";
import { useNavigate } from "react-router-dom";
import { VaultTransactionsTable } from "../../../containers/tables/vaulttransactionstable";
import { useQuery } from "@tanstack/react-query";
import Cookies from "universal-cookie";
import { retrieveAllVaultTransactionService } from "../../../utils/api/tellermanagement/vault/retrieveAllVaultTransactions";
import { retrieveUserProfileService } from "../../../utils/api/usermanagement/profile/retrieveUser";
import { retrieveInstitutionByIdService } from "../../../utils/outbound/institution/retrieveInstitutionById";
import { retrieveCurrencyByIdService } from "../../../utils/outbound/currency/retrieveCurrencyById";
import { retrieveChartOfAccountByGLCodeService } from "../../../utils/api/accounting/chartofaccount/retrieveChartOfAccountByGLCode";
import { retrieveTellerService } from "../../../utils/api/usermanagement/assignteller/retrieveTeller";
import { BaseFieldSet } from "../../../components/form/fieldset/styled";
import { BaseLabel } from "../../../components/form/label/styled";
import { BaseInput } from "../../../components/form/input/styled";
import { downloadAllVaultTransactionService } from "../../../utils/api/tellermanagement/vault/downloadAllVaultTransactions";

export const Vault = () => {
	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const navigate = useNavigate();
	const { setActiveNavItem } = useContext(AppContext);

	const [isDownloading, setIsDownloading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [isFiltering, setIsFiltering] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [refreshKey, setRefreshKey] = useState(0);
	const [formDetails, setFormDetails] = useState({
		startDate: "",
		endDate: "",
	});
	const [allVaultTransactions, setAllVaultTransactions] = useState<
		Record<string, any>[] | null
	>(null);
	const [paginationIndex, setPaginationIndex] = useState({
		page: "1",
		perPage: "10",
		totalPages: "1",
	});

	useEffect(() => {
		setActiveNavItem("Teller Management");
	}, [setActiveNavItem]);

	const { data: loggedInTeller } = useQuery({
		queryKey: ["loggedInUser", TOKEN],
		queryFn: async () => {
			return await retrieveUserProfileService(TOKEN);
		},
		enabled: !!TOKEN,
	});

	const { data: currency } = useQuery({
		queryKey: [
			"currencySymbol",
			loggedInTeller?._id,
			loggedInTeller?.institution,
		],
		queryFn: async () => {
			if (!loggedInTeller) return null;
			const institution = await retrieveInstitutionByIdService(
				loggedInTeller._id,
				loggedInTeller.institution,
			);
			const currencyDetails = await retrieveCurrencyByIdService(
				loggedInTeller._id,
				institution?.currency,
			);
			return currencyDetails?.symbol;
		},
		enabled: !!loggedInTeller,
	});

	const { data: tillBalance } = useQuery({
		queryKey: ["tillBalance", TOKEN],
		queryFn: async () => {
			const assignTeller = await retrieveTellerService(
				TOKEN,
				loggedInTeller?.id,
			);
			const response = await retrieveChartOfAccountByGLCodeService(
				TOKEN,
				assignTeller?.tillNumber,
			);
			return response.glBalance;
		},
		enabled: !!TOKEN && !!loggedInTeller,
	});

	const handleNavigateToPostTransaction = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		e.preventDefault();
		return navigate(`/tellermanagement/vault/posttransaction`);
	};

	const handleChange = (
		e:
			| React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
			| SelectChangeEvent<unknown>,
	) => {
		const { name, value } = e.target;
		setFormDetails((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const fetchTransactions = useCallback(async () => {
		setError(null);
		setIsFiltering(true);
		setAllVaultTransactions(null);
		try {
			const response = await retrieveAllVaultTransactionService(
				TOKEN,
				formDetails,
				{
					page: paginationIndex.page,
					perPage: paginationIndex.perPage,
				},
			);
			if (!Array.isArray(response) && response?.meta) {
				setPaginationIndex((prev) => ({
					...prev,
					totalPages: String(response.meta.totalPages ?? "1"),
				}));
			}
			if (response?.data?.length > 0) {
				setAllVaultTransactions(response.data);
			} else {
				setError("No vault transactions found for selected filters.");
			}
		} catch (error) {
			setError("No vault transactions found for selected filters.");
			console.error("Vault transactions generation failed:", error);
		} finally {
			setIsLoading(false);
			setIsFiltering(false);
		}
	}, [TOKEN, formDetails, paginationIndex.page, paginationIndex.perPage]);

	useEffect(() => {
		fetchTransactions();
	}, [fetchTransactions, refreshKey]);

	const handleFilterSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		setError(null);
		setIsLoading(true);
		setPaginationIndex((prev) => ({ ...prev, page: "1" }));
		setRefreshKey((prev) => prev + 1);
	};

	const handleVaultActionButtonClick = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		e.preventDefault();
		setIsDownloading(true);
		try {
			setError(null);
			const response = await downloadAllVaultTransactionService(
				TOKEN,
				formDetails,
			);
			if (!response.ok) {
				const errorData = await response.json().catch(() => null);
				setError(
					errorData?.message ??
						"Unable to download the call-over report. Please try again.",
				);
				return;
			}
			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = "call_over_report.pdf";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error: any) {
			setError(error?.message);
			console.error("Call over report download failed:", error);
		} finally {
			setIsDownloading(false);
		}
	};

	const handlePagination = (
		e: React.MouseEvent<HTMLButtonElement>,
		type: "previous" | "next",
	) => {
		e.preventDefault();
		setPaginationIndex((prev) => {
			const currentPage = Number(prev.page);
			const totalPages = Number(prev.totalPages);

			const page =
				type === "previous"
					? Math.max(1, currentPage - 1)
					: Math.min(totalPages, currentPage + 1);

			return { ...prev, page: String(page) };
		});
	};

	return (
		<AppLayout pageId={"Vault"} pageTitle={"Teller Transaction"}>
			<VaultWrapper>
				<Card
					sx={{
						borderRadius: "1rem",
						maxWidth: "377px",
					}}
				>
					<CardContent className="card-content">
						<Stack
							height={"100%"}
							padding={"var(--basic-padding)"}
							gap={"var(--flex-gap)"}
						>
							<Box>
								<Typography
									variant="h2"
									fontFamily={"Poppins"}
									fontWeight={600}
									fontSize={20}
									lineHeight={"normal"}
									color="var(--dark-color)"
								>
									{loggedInTeller?.firstName} {loggedInTeller?.lastName}
								</Typography>
							</Box>
							<Stack>
								<Box>
									<Typography
										variant="h2"
										fontFamily={"Poppins"}
										fontWeight={400}
										fontSize={16}
										lineHeight={"normal"}
										color="var(--primary-color)"
									>
										Till Closing Balance:
									</Typography>
								</Box>
								<Box>
									<Typography
										variant="h2"
										fontFamily={"Poppins"}
										fontWeight={600}
										fontSize={20}
										lineHeight={"normal"}
										color="var(--dark-color)"
									>
										{currency || ""}
										{(() => {
											const value = tillBalance ?? 0;
											const parts = value.toString().split(".");
											if (parts.length === 1)
												return value.toLocaleString(undefined, {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												});
											const decimalPlaces = parts[1].length;
											return value.toLocaleString(undefined, {
												minimumFractionDigits: 2,
												maximumFractionDigits: decimalPlaces,
											});
										})()}
									</Typography>
								</Box>
							</Stack>
						</Stack>
					</CardContent>
				</Card>
				<Stack
					direction={{ miniTablet: "row" }}
					gap={{
						mobile: "var(--flex-gap)",
						miniTablet: "calc(var(--flex-gap)/3)",
					}}
					width={{ miniTablet: "100%" }}
				>
					<Box overflow={"hidden"}>
						<BaseButton
							variant="contained"
							bgcolor="var(--primary-color)"
							padding="calc(var(--basic-padding)/4) calc(var(--basic-padding))"
							sx={{ width: { mobile: "-webkit-fill-available" } }}
							onClick={handleNavigateToPostTransaction}
						>
							<Typography
								variant={"button"}
								fontFamily={"inherit"}
								fontWeight={"inherit"}
								fontSize={"inherit"}
								lineHeight={"inherit"}
								color={"inherit"}
								textTransform={"inherit"}
							>
								Create Teller Transaction
							</Typography>
						</BaseButton>
					</Box>
					<Box overflow={"hidden"}>
						<BaseButton
							variant="contained"
							bgcolor="var(--primary-color)"
							padding="calc(var(--basic-padding)/4) calc(var(--basic-padding))"
							sx={{ width: { mobile: "-webkit-fill-available" } }}
							onClick={handleVaultActionButtonClick}
						>
							{isDownloading ? (
								<CircularProgress color="inherit" className="loader" />
							) : (
								<Typography
									variant={"button"}
									fontFamily={"inherit"}
									fontWeight={"inherit"}
									fontSize={"inherit"}
									lineHeight={"inherit"}
									color={"inherit"}
									textTransform={"inherit"}
								>
									CallOver Report
								</Typography>
							)}
						</BaseButton>
					</Box>
				</Stack>
				<form className="filter-bar">
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">Start Date</BaseLabel>
						<BaseInput
							type="date"
							name="startDate"
							fontweight={400}
							value={formDetails.startDate}
							colour="var(--semi-gray-color)"
							sx={{ width: "-webkit-fill-available" }}
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">End Date</BaseLabel>
						<BaseInput
							type="date"
							name="endDate"
							fontweight={400}
							value={formDetails.endDate}
							colour="var(--semi-gray-color)"
							sx={{ width: "-webkit-fill-available" }}
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
					<Box component={"div"} className="filter-button-box">
						<BaseButton
							variant="contained"
							padding="calc(var(--basic-padding)/4) calc(var(--basic-padding))"
							sx={{
								width: { mobile: "-webkit-fill-available", miniTablet: "auto" },
							}}
							onClick={handleFilterSubmit}
						>
							{isLoading ? (
								<CircularProgress color="inherit" className="loader" />
							) : (
								<Typography
									variant={"button"}
									fontFamily={"inherit"}
									fontWeight={"inherit"}
									fontSize={"inherit"}
									lineHeight={"inherit"}
									color={"inherit"}
									textTransform={"inherit"}
								>
									Filter
								</Typography>
							)}
						</BaseButton>
					</Box>
				</form>
				{error && (
					<Box>
						<Typography
							fontFamily={"Inter"}
							fontWeight={"600"}
							fontSize={14}
							lineHeight={"normal"}
							color={"#FF0000"}
							whiteSpace={"normal"}
						>
							{error}
						</Typography>
					</Box>
				)}
				<Box component={"div"} className="vault-table-box">
					<VaultTransactionsTable rows={allVaultTransactions ?? []} />
				</Box>
				<Grid2 container component={"div"} spacing={"var(--flex-gap)"}>
					<Grid2
						size={{ mobile: 12, laptop: 8 }}
						display={{ mobile: "none", laptop: "grid" }}
					></Grid2>
					<Grid2 size={{ mobile: 12, laptop: 4 }}>
						<Stack
							direction={"row"}
							overflow={"hidden"}
							borderRadius={"6px"}
							alignItems={"center"}
							justifyContent={"space-around"}
							border={"1px solid var(--input-field-border-color)"}
						>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									disabled={
										parseInt(paginationIndex.page, 10) === 1 || isFiltering
									}
									onClick={(e) => handlePagination(e, "previous")}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Previous
									</Typography>
								</BaseButton>
							</Box>
							<Box
								display={"flex"}
								overflow={"hidden"}
								bgcolor={"var(--primary-color)"}
								padding={"calc(var(--basic-padding)/2)"}
							>
								<Typography
									variant="h3"
									fontFamily={"Roboto"}
									fontWeight={600}
									fontSize={10}
									lineHeight={"normal"}
									color="var(--light-color)"
								>
									{`${paginationIndex.page} / ${paginationIndex.totalPages}`}
								</Typography>
							</Box>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									onClick={(e) => handlePagination(e, "next")}
									disabled={
										paginationIndex.page === paginationIndex.totalPages ||
										isFiltering
									}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Next
									</Typography>
								</BaseButton>
							</Box>
						</Stack>
					</Grid2>
				</Grid2>
			</VaultWrapper>
		</AppLayout>
	);
};
